import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { theme } from '../../theme';
import { CATEGORIES, REQUEST_STATUSES, QUOTE_STATUSES } from '../../constants';
import { formatTimeAgo, formatPrice, formatDays } from '../../utils/formatters';
import { useApi } from '../../hooks/useApi';

export function RequestDetailScreen({ requestId, onNavigate }) {
  const api = useApi();
  const { user } = useAuth();
  const [request, setRequest] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acceptingId, setAcceptingId] = useState(null);

  useEffect(() => {
    if (!requestId) { setLoading(false); return; }
    let cancelled = false;
    async function fetchData() {
      setLoading(true);
      try {
        const res = await api.requests.get(requestId);
        if (!cancelled) {
          setRequest(res.data || res);
          setQuotes(res.data?.quotes || res.quotes || []);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [requestId]);

  const handleAcceptQuote = async (quoteId) => {
    setAcceptingId(quoteId);
    try {
      await api.quotes.respond(quoteId, { status: 'accepted' });
      // Refresh data
      const res = await api.requests.get(requestId);
      setRequest(res.data || res);
      setQuotes(res.data?.quotes || res.quotes || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setAcceptingId(null);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '16px', textAlign: 'center', marginTop: '40px' }}>
        <p style={{ color: theme.textMuted }}>Hleð...</p>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div style={{ padding: '16px' }}>
        <button
          onClick={() => onNavigate('browse')}
          style={{ background: 'none', border: 'none', color: theme.accent, cursor: 'pointer', marginBottom: '16px' }}
        >
          ← Til baka
        </button>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p style={{ fontSize: '40px', marginBottom: '12px' }}>📋</p>
          <p style={{ color: theme.text, fontSize: '16px', fontWeight: 600 }}>
            {error || 'Beiðni finnst ekki'}
          </p>
        </div>
      </div>
    );
  }

  const cat = CATEGORIES.find(c => c.id === request.category);
  const status = REQUEST_STATUSES[request.status] || REQUEST_STATUSES.open;
  const isOwner = user?.sub === request.user_id;

  return (
    <div style={{ padding: '16px' }}>
      <button
        onClick={() => onNavigate('browse')}
        style={{ background: 'none', border: 'none', color: theme.accent, cursor: 'pointer', marginBottom: '16px' }}
      >
        ← Til baka
      </button>

      {/* Request Card */}
      <div style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: theme.radius,
        padding: '16px',
        marginBottom: '16px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <span style={{
            fontSize: '12px',
            padding: '4px 10px',
            borderRadius: '12px',
            background: `${status.color}20`,
            color: status.color,
            fontWeight: 600,
          }}>
            {status.label}
          </span>
          <span style={{ fontSize: '12px', color: theme.textMuted }}>
            {formatTimeAgo(request.created_at)}
          </span>
        </div>
        <h2 style={{ fontSize: '18px', fontWeight: 700, color: theme.text, marginBottom: '8px' }}>
          {request.title}
        </h2>
        <p style={{ fontSize: '14px', color: theme.textSecondary, lineHeight: 1.5 }}>
          {request.description}
        </p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '8px',
          marginTop: '12px',
        }}>
          <div style={metaBox}>
            <span style={metaLabel}>Flokkur</span>
            <span style={metaValue}>{cat?.icon} {cat?.label}</span>
          </div>
          <div style={metaBox}>
            <span style={metaLabel}>Fjöldi</span>
            <span style={metaValue}>{request.quantity} stk.</span>
          </div>
          {request.material_preference && (
            <div style={metaBox}>
              <span style={metaLabel}>Efni</span>
              <span style={metaValue}>{request.material_preference}</span>
            </div>
          )}
          {request.area && (
            <div style={metaBox}>
              <span style={metaLabel}>Svæði</span>
              <span style={metaValue}>{request.area}</span>
            </div>
          )}
        </div>

        {request.file_url && (
          <a
            href={api.files.url(request.file_url)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              marginTop: '12px',
              padding: '8px 14px',
              background: theme.accentBg,
              border: `1px solid ${theme.accent}`,
              borderRadius: theme.radiusSm,
              color: theme.accent,
              fontSize: '13px',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            📎 Skoða skrá
          </a>
        )}
      </div>

      {/* Quotes Section */}
      <h3 style={{ fontSize: '16px', fontWeight: 700, color: theme.text, marginBottom: '12px' }}>
        Tilboð ({quotes.length})
      </h3>
      {quotes.length === 0 ? (
        <div style={{
          background: theme.bgCard,
          border: `1px solid ${theme.border}`,
          borderRadius: theme.radius,
          padding: '24px',
          textAlign: 'center',
        }}>
          <p style={{ color: theme.textSecondary, fontSize: '14px' }}>
            Engin tilboð enn — bíðum eftir makers!
          </p>
        </div>
      ) : (
        quotes.map(q => {
          const qStatus = QUOTE_STATUSES[q.status] || QUOTE_STATUSES.pending;
          return (
            <div key={q.id} style={{
              background: theme.bgCard,
              border: `1px solid ${theme.border}`,
              borderRadius: theme.radius,
              padding: '16px',
              marginBottom: '8px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontWeight: 600, color: theme.text }}>{formatPrice(q.price)}</span>
                <span style={{
                  fontSize: '12px',
                  padding: '2px 8px',
                  borderRadius: '8px',
                  background: `${qStatus.color}20`,
                  color: qStatus.color,
                }}>
                  {qStatus.label}
                </span>
              </div>
              {q.maker_name && (
                <p style={{ fontSize: '13px', color: theme.accent, marginBottom: '4px', cursor: 'pointer' }}
                   onClick={() => onNavigate('maker-profile', q.maker_id)}>
                  🔧 {q.maker_name}
                </p>
              )}
              {q.message && <p style={{ fontSize: '13px', color: theme.textSecondary }}>{q.message}</p>}
              <p style={{ fontSize: '12px', color: theme.textMuted, marginTop: '4px' }}>
                {formatDays(q.estimated_days)}
              </p>
              {isOwner && q.status === 'pending' && (
                <button
                  onClick={() => handleAcceptQuote(q.id)}
                  disabled={acceptingId === q.id}
                  style={{
                    marginTop: '10px',
                    width: '100%',
                    padding: '10px',
                    background: theme.success,
                    color: '#fff',
                    border: 'none',
                    borderRadius: theme.radiusSm,
                    fontWeight: 600,
                    cursor: acceptingId === q.id ? 'wait' : 'pointer',
                    opacity: acceptingId === q.id ? 0.6 : 1,
                  }}
                >
                  {acceptingId === q.id ? 'Samþykki...' : 'Samþykkja tilboð'}
                </button>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}

const metaBox = {
  background: theme.bgInput,
  borderRadius: theme.radiusSm,
  padding: '10px',
};

const metaLabel = {
  fontSize: '11px',
  color: theme.textMuted,
  display: 'block',
  marginBottom: '2px',
};

const metaValue = {
  fontSize: '13px',
  color: theme.text,
  fontWeight: 500,
};
